/**
 * w-unique-products Section JavaScript
 * Handles scroll-based animation for the decoration line and item activation
 */

class UniqueProductsSection {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.w-unique-products__content');
    this.decorationLine = section.querySelector('.w-unique-products__decoration-line');
    this.items = Array.from(section.querySelectorAll('.w-unique-products__item'));
    this.progressFill = null;

    if (!this.decorationLine || this.items.length === 0) return;

    this.init();
  }

  init() {
    // Create progress fill element
    this.createProgressFill();

    // Calculate decoration line height
    this.calculateDecorationLineHeight();

    // Setup scroll listener for progress bar and items activation
    this.setupScrollListener();
  }

  createProgressFill() {
    // Create a fill element inside the decoration line
    this.progressFill = document.createElement('div');
    this.progressFill.className = 'w-unique-products__decoration-line-fill';
    this.decorationLine.appendChild(this.progressFill);
  }

  calculateDecorationLineHeight() {
    if (!this.decorationLine || this.items.length === 0) return;

    const firstItem = this.items[0];
    const lastItem = this.items[this.items.length - 1];

    if (!firstItem || !lastItem) return;

    // Calculate distance between first and last item
    const firstItemOffsetTop = firstItem.offsetTop;
    const lastItemOffsetTop = lastItem.offsetTop;

    // Height = distance between items
    const calculatedHeight = lastItemOffsetTop - firstItemOffsetTop;

    // Update the decoration line height
    this.decorationLine.style.height = `${calculatedHeight}px`;
  }

  setupScrollListener() {
    let ticking = false;

    const updateProgress = () => {
      if (!this.container || this.items.length === 0) return;

      const firstItem = this.items[0];
      const lastItem = this.items[this.items.length - 1];

      if (!firstItem || !lastItem) return;

      const viewportHeight = window.innerHeight;
      const viewportCenter = viewportHeight / 2;
      const progressTrigger = viewportCenter;

      const firstItemRect = firstItem.getBoundingClientRect();
      const firstItemTop = firstItemRect.top;
      const lastItemRect = lastItem.getBoundingClientRect();
      const lastItemTop = lastItemRect.top;

      let scrollProgress = 0;

      // Calculate progress based on viewport center position
      if (firstItemTop <= progressTrigger && lastItemTop >= progressTrigger) {
        const totalDistance = firstItemTop - lastItemTop;
        const currentDistance = firstItemTop - progressTrigger;
        scrollProgress = Math.max(0, Math.min(1, currentDistance / totalDistance));
      } else if (firstItemTop > progressTrigger) {
        scrollProgress = 0;
      } else if (lastItemTop < progressTrigger) {
        scrollProgress = 1;
      }

      // Update progress fill height (percentage-based like timeline_vso)
      const progressPercent = scrollProgress * 100;
      if (this.progressFill) {
        this.progressFill.style.height = `${progressPercent}%`;
      }

      // Update items states
      this.updateItemsStates(scrollProgress);

      ticking = false;
    };

    const requestUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(updateProgress);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });

    // Handle resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Recalculate line height on resize
        this.calculateDecorationLineHeight();
        // Update progress
        requestUpdate();
      }, 150);
    });

    // Initial update
    updateProgress();
  }

  updateItemsStates(scrollProgress) {
    if (this.items.length === 0 || !this.decorationLine) return;

    const firstItem = this.items[0];
    if (!firstItem) return;

    // Get the total height of the decoration line
    const lineHeight = parseFloat(this.decorationLine.style.height) || 336;

    // Calculate actual progress bar height in pixels
    const progressBarHeight = lineHeight * scrollProgress;

    // Activate items based on progress bar position
    this.items.forEach((item, index) => {
      // Calculate the position of each item relative to the first item
      const itemPosition = item.offsetTop - firstItem.offsetTop;

      // Activate if the progress bar has reached this item
      const isActive = progressBarHeight >= itemPosition;
      item.classList.toggle('is-active', isActive);
    });
  }
}

// Initialize all unique products sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="w-unique-products"]');
  sections.forEach(section => {
    new UniqueProductsSection(section);
  });
});

// Shopify section editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="w-unique-products"]');
    if (section) {
      new UniqueProductsSection(section);
    }
  });
}
